import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins once at app initialization
gsap.registerPlugin(ScrollTrigger);

/**
 * Default GSAP configuration for consistency across animations.
 */
gsap.defaults({
  ease: 'power3.out',
  duration: 0.8,
});

/**
 * Create a responsive ScrollTrigger-powered animation.
 * Uses matchMedia to disable/simplify on mobile.
 */
export function createScrollAnimation(trigger, animation, options = {}) {
  const {
    start = 'top 85%',
    end = 'bottom 20%',
    toggleActions = 'play none none reverse',
    // eslint-disable-next-line no-unused-vars
    mobileSimplify = true,
  } = options;

  return ScrollTrigger.create({
    trigger,
    start,
    end,
    toggleActions,
    animation,
    ...options,
  });
}

/**
 * Split text into individual characters/words for animation.
 * Custom implementation (no GSAP SplitText plugin required).
 * Returns the created elements for GSAP targeting.
 */
export function splitText(element, type = 'chars') {
  if (!element) return [];

  const text = element.textContent;
  element.textContent = '';
  element.setAttribute('aria-label', text);

  // Detect RTL content (Arabic/Hebrew) to preserve correct text direction
  const isRTL = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u0590-\u05FF]/.test(text);

  if (type === 'words') {
    if (isRTL) {
      // For RTL: use a flex container with row-reverse to keep proper word order
      // while allowing GSAP transforms on each word
      const wrapper = document.createElement('span');
      wrapper.style.display = 'flex';
      wrapper.style.flexWrap = 'wrap';
      wrapper.style.flexDirection = 'row-reverse';
      wrapper.style.justifyContent = 'center';
      wrapper.style.gap = '0.3em';
      wrapper.style.direction = 'rtl';
      wrapper.setAttribute('aria-hidden', 'true');
      element.appendChild(wrapper);

      const words = text.split(/\s+/);
      return words.map((word) => {
        const span = document.createElement('span');
        span.textContent = word;
        span.style.display = 'inline-block';
        span.style.direction = 'rtl';
        span.style.unicodeBidi = 'bidi-override';
        span.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(span);
        return span;
      });
    }

    const words = text.split(' ');
    return words.map((word, i) => {
      const span = document.createElement('span');
      span.textContent = word + (i < words.length - 1 ? '\u00A0' : '');
      span.style.display = 'inline-block';
      span.style.overflow = 'hidden';
      span.setAttribute('aria-hidden', 'true');
      element.appendChild(span);
      return span;
    });
  }

  if (type === 'chars') {
    // Group chars by word so the browser never breaks mid-word.
    const words = text.split(' ');
    const charSpans = [];
    words.forEach((word, wi) => {
      const wordWrap = document.createElement('span');
      wordWrap.style.display = 'inline-block';
      wordWrap.style.whiteSpace = 'nowrap';
      wordWrap.setAttribute('aria-hidden', 'true');
      word.split('').forEach((char) => {
        const span = document.createElement('span');
        span.textContent = char;
        span.style.display = 'inline-block';
        span.setAttribute('aria-hidden', 'true');
        wordWrap.appendChild(span);
        charSpans.push(span);
      });
      element.appendChild(wordWrap);
      if (wi < words.length - 1) {
        element.appendChild(document.createTextNode(' '));
      }
    });
    return charSpans;
  }

  if (type === 'lines') {
    const words = text.split(' ');
    const wrapper = document.createElement('span');
    wrapper.style.display = 'inline';
    wrapper.setAttribute('aria-hidden', 'true');

    const lineWrappers = [];
    let currentLine = document.createElement('span');
    currentLine.style.display = 'inline-block';
    currentLine.style.overflow = 'hidden';

    words.forEach((word, i) => {
      const wordSpan = document.createElement('span');
      wordSpan.textContent = word + (i < words.length - 1 ? ' ' : '');
      wordSpan.style.display = 'inline-block';
      currentLine.appendChild(wordSpan);
    });

    wrapper.appendChild(currentLine);
    lineWrappers.push(currentLine);
    element.appendChild(wrapper);
    return lineWrappers;
  }

  return [];
}

/**
 * Create a staggered reveal animation for a set of elements.
 */
export function staggerReveal(elements, options = {}) {
  const {
    y = 40,
    opacity = 0,
    duration = 0.8,
    stagger = 0.1,
    ease = 'power3.out',
    delay = 0,
  } = options;

  return gsap.from(elements, {
    y,
    opacity,
    duration,
    stagger,
    ease,
    delay,
  });
}

/**
 * Create a counter animation (count up from 0 to target).
 */
export function animateCounter(element, target, options = {}) {
  const {
    duration = 2,
    ease = 'power2.out',
    suffix = '',
    prefix = '',
  } = options;

  const obj = { value: 0 };

  return gsap.to(obj, {
    value: target,
    duration,
    ease,
    onUpdate: () => {
      element.textContent = prefix + Math.round(obj.value) + suffix;
    },
  });
}

/**
 * Magnetic hover effect for buttons.
 */
export function createMagneticEffect(element, strength = 0.3) {
  const prefersReduced = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (prefersReduced) return () => {};

  const handleMove = (e) => {
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;

    gsap.to(element, {
      x: x * strength,
      y: y * strength,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleLeave = () => {
    gsap.to(element, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: 'elastic.out(1, 0.3)',
    });
  };

  element.addEventListener('mousemove', handleMove);
  element.addEventListener('mouseleave', handleLeave);

  return () => {
    element.removeEventListener('mousemove', handleMove);
    element.removeEventListener('mouseleave', handleLeave);
  };
}

export { gsap, ScrollTrigger };
